import { debounce } from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import {
  FormCheckboxFieldDS,
  FormDateFieldDS,
  FormRadioGroupDS,
  FormTextFieldDS,
} from 'components/reusable/form/FormFields'
import {
  Banner,
  Button,
  ButtonGroup,
  CollapsibleContainer,
  SolidCircleCheckIcon,
  Tooltip,
  useBreakpoint
} from 'slab'
import { type CoveredParticipant } from 'types'
import { calculateAgeAtDate, getFormattedDate } from 'utils'

import { useOnboarding } from '../OnboardingContext'
import { type AdditionalParticipant, type ParticipantFormProps } from '../types'
import { hasExistingOnThatchEnrollments } from '../utils'
import { OnboardingGenericErrorMessage } from './OnboardingGenericErrorMessage'

// Options for debouncing - defined outside component to maintain stable reference
const DEBOUNCE_OPTIONS = { leading: true, trailing: false }

// === Remove dependent button ===
const RemoveParticipantButton = ({
  participantId,
  handleError,
  onTriggerModal,
  modalSubmitted,
  modalClosed,
  enrollmentsPresent,
}: {
  participantId: string;
  handleError: (error: GrapeAPIErrorType) => void;
  onTriggerModal: () => void;
  modalSubmitted: boolean;
  modalClosed: boolean;
  enrollmentsPresent: boolean | undefined;
}) => {
  const {
    deleteAdditionalParticipant,
    refreshAdditionalParticipants,
    refreshOnboardingInfo,
  } = useOnboarding()
  const [participantToBeDeleted, setParticipantToBeDeleted] = useState<
    string | null
  >(null)

  useEffect(() => {
    if (modalSubmitted && participantToBeDeleted) {
      handleDeleteParticipant()
    }
  }, [modalSubmitted, participantToBeDeleted])

  useEffect(() => {
    if (modalClosed && participantToBeDeleted) {
      setParticipantToBeDeleted(null)
    }
  }, [modalClosed, participantToBeDeleted])

  const mutation = deleteAdditionalParticipant(participantId)

  const maybeDeleteParticipant = () => {
    if (enrollmentsPresent) {
      setParticipantToBeDeleted(participantId)
      onTriggerModal()
    } else {
      handleDeleteParticipant()
    }
    window.segment?.track('[EE] Remove dependent clicked', {
      participant_id: participantId,
    })
  }

  const handleDeleteParticipant = () => {
    mutation.mutate(undefined, {
      onSuccess: () => {
        refreshAdditionalParticipants()
        refreshOnboardingInfo()
      },
      onError: (error) => {
        handleError(error)
      },
    })
  }

  return (
    <Button
      size='large'
      onClick={maybeDeleteParticipant}
      variant='tertiary-destructive'
      disabled={mutation.isPending}
    >
      Remove dependent
    </Button>
  )
}

// === CoveredParticipantCard ===
// This component will only be used for additional participants.
export function CoveredParticipantCard ({
  participant,
  handlePostAction,
  onTriggerModal,
  modalSubmitted,
  modalClosed,
  onInfoSaved,
  hasExistingSpouse,
}: {
  participant?: AdditionalParticipant;
  handlePostAction?: () => void;
  onTriggerModal: () => void;
  modalSubmitted: boolean;
  modalClosed: boolean;
  onInfoSaved?: (isSuccessful: boolean) => void;
  hasExistingSpouse: boolean;
}) {
  const {
    onboardingData,
    createAdditionalParticipant,
    updateAdditionalParticipant,
    refreshAdditionalParticipants,
    refreshOnboardingInfo,
  } = useOnboarding()
  const [submitError, setSubmitError] = useState<React.ReactElement | null>(
    null
  )
  const [allInfoValid, setAllInfoValid] = useState<boolean>(true)
  const [open, setOpen] = useState<boolean>(!participant)
  const [pendingFormData, setPendingFormData] =
    useState<ParticipantFormProps | null>(null)
  const [showDisabilityCheckbox, setShowDisabilityCheckbox] =
    useState<boolean>(false)

  const { breakpointIsAtLeast } = useBreakpoint()

  /* ============ Debounced onSubmit to prevent duplicate submissions ============
   * We use debounce with leading=true to prevent duplicate submissions.
   * We have cases in production where 2 requests are sent within 1ms of each other.
   * Because of this short window, we cannot rely on mutation.isPending to prevent duplicates.
   */

  // Use a ref to ensure the debounced function always calls the latest onSubmit.
  // Otherwise, the debounced function would capture a stale closure.
  const onSubmitRef = useRef<(formData: ParticipantFormProps) => void>()

  useEffect(() => {
    onSubmitRef.current = onSubmit
  })

  // Memoize the debounced function to prevent it from being recreated (and the timer from resetting) on each render
  const debouncedOnSubmit = useMemo(
    () => debounce(
      (formData: ParticipantFormProps) => {
        onSubmitRef.current?.(formData)
      },
      300,
      DEBOUNCE_OPTIONS
    ),
    []
  )

  useEffect(() => {
    return () => {
      debouncedOnSubmit.cancel()
    }
  }, [debouncedOnSubmit])

  // === useEffects ===
  useEffect(() => {
    // if there are any missing fields, then it would be applicable only for covered participants at this point
    if (
      onboardingData?.missing_fields?.allowance?.some(
        // additional_participant.{id}.field_name
        (field) => field.split('.')[1] === participant?.id
      )
    ) {
      setAllInfoValid(false)
    }
  }, [onboardingData])

  useEffect(() => {
    if (modalSubmitted && pendingFormData) {
      debouncedOnSubmit(pendingFormData)
    }
  }, [modalSubmitted, pendingFormData, debouncedOnSubmit])

  useEffect(() => {
    if (modalClosed && pendingFormData) {
      setPendingFormData(null)
    }
  }, [modalClosed, pendingFormData])

  // === Get defaults for card ===

  const getDefaultRelationship = () => {
    if (hasExistingSpouse) {
      return 'child'
    }
    return 'spouse'
  }

  // If we're editing an existing participant and it's a spouse, don't disable
  // Otherwise, use the prop from parent to determine if there are existing spouses
  const isSpouseOptionDisabled =
    participant && participant.relationship === 'spouse' ? false : hasExistingSpouse

  const getDefaultValues = (cp?: CoveredParticipant): ParticipantFormProps => {
    return {
      given_name: cp?.given_name || '',
      family_name: cp?.family_name || '',
      dob: cp?.dob || '',
      relationship: cp?.relationship || getDefaultRelationship(),
      tobacco: cp?.tobacco || false,
      has_disability: cp?.has_disability || null,
    }
  }

  const massageErrorMessages = (errorMessages: string[]) => {
    return errorMessages.map((message) => {
      if (message.includes('25 years')) {
        return 'children over 25 years old must have a disability to be on your plan'
      }
      return message
    })
  }

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    clearErrors,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<ParticipantFormProps>({
    defaultValues: getDefaultValues(participant),
    mode: 'onChange',
  })

  // This hook is needed in case the data refreshes.
  useEffect(() => {
    reset(getDefaultValues(participant))
  }, [participant, getDefaultRelationship()])

  const onHandleError = (error: GrapeAPIErrorType) => {
    if (error.status === 422) {
      const fieldErrors = error.fieldErrors!
      Object.entries(fieldErrors).forEach(([field, errorMessages]) => {
        const massagedErrors = massageErrorMessages(errorMessages)
        setError(field as keyof ParticipantFormProps, {
          type: 'manual',
          message: massagedErrors.join(', '),
        })
      })
    } else {
      const errorMessage = <OnboardingGenericErrorMessage error={error} />
      setSubmitError(errorMessage)
    }
  }

  const enrollmentsPresent = hasExistingOnThatchEnrollments(onboardingData)

  const mutation = participant
    ? updateAdditionalParticipant(participant.id)
    : createAdditionalParticipant

  const relationship = watch('relationship')
  const dob = watch('dob')
  const givenName = watch('given_name')
  const hasDisability = watch('has_disability')

  // === Disability checkbox logic ===
  const decideIfShowDisabilityCheckbox = () => {
    if (shouldShowDisabilityCheckbox()) {
      setShowDisabilityCheckbox(true)
    } else {
      setShowDisabilityCheckbox(false)
    }
  }

  useEffect(() => {
    if (dob) {
      // We only show disability check if the year has four digits
      // So that we can prevent showing the disability check while people are entering the date
      if (new Date(dob).getFullYear() >= 1000) {
        decideIfShowDisabilityCheckbox()
      } else {
        // We hide the disability check, the year will still be validated
        // based on the validation rule from DOB field below
        setShowDisabilityCheckbox(false)
      }
    } else {
      // If there is no DOB, we hide the disability check
      setShowDisabilityCheckbox(false)
    }
  }, [relationship, dob, decideIfShowDisabilityCheckbox])

  const soonestCoverageStartDate =
    onboardingData?.member?.soonest_coverage_start_date

  // Helper function to check if a child is over 25 at coverage start date
  const isChildOver25AtCoverageStart = (
    birthDate: string | null | undefined
  ): boolean => {
    if (!birthDate) return false
    if (!soonestCoverageStartDate) return false

    const age = calculateAgeAtDate(birthDate, soonestCoverageStartDate)
    return age > 25
  }

  const shouldShowDisabilityCheckbox = () => {
    if (relationship === 'child' && dob && soonestCoverageStartDate) {
      return isChildOver25AtCoverageStart(dob)
    }
    return false
  }

  const shouldDisableSubmitButton = () => {
    if (shouldShowDisabilityCheckbox() && !hasDisability) {
      return true
    }
    return false
  }

  useEffect(() => {
    if (shouldDisableSubmitButton()) {
      setError('has_disability', {
        type: 'manual',
        message: 'You must check this box or remove this dependent to proceed',
      })
    } else {
      clearErrors('has_disability')
    }
  }, [shouldDisableSubmitButton()])

  // === Submit form logic ===
  const maybeSubmitForm = (formData: ParticipantFormProps) => {
    if (dirtyFields.dob && enrollmentsPresent) {
      setPendingFormData(formData)
      onTriggerModal()
    } else {
      debouncedOnSubmit(formData)
    }
  }

  const onSubmit = (formData: ParticipantFormProps) => {
    // reset has_disability if the relationship is spouse
    if (formData.relationship === 'spouse' && formData.has_disability) {
      formData.has_disability = null
    }

    // reset has_disability if the child is not over 25 at the coverage start date
    if (formData.relationship === 'child' && formData.has_disability) {
      if (!isChildOver25AtCoverageStart(formData.dob)) {
        formData.has_disability = null
      }
    }

    // Validate that has_disability is checked for children over 25
    if (
      formData.relationship === 'child' &&
      isChildOver25AtCoverageStart(formData.dob) &&
      !formData.has_disability
    ) {
      setError('has_disability', {
        type: 'manual',
        message: 'You must check this box or remove this dependent to proceed',
      })
      return
    }

    mutation.mutate(formData, {
      onSuccess: () => {
        refreshAdditionalParticipants()
        refreshOnboardingInfo()
        handlePostAction?.()
        setOpen(false)
      },
      onError: (error) => {
        onHandleError(error)
      },
    })
  }

  // === Render ===

  // Buttons
  function renderButtons () {
    const removeButton = participant && (
      <RemoveParticipantButton
        participantId={participant.id}
        handleError={onHandleError}
        onTriggerModal={onTriggerModal}
        modalSubmitted={modalSubmitted}
        modalClosed={modalClosed}
        enrollmentsPresent={enrollmentsPresent}
      />
    )

    const cancelButton = (isDirty || !participant) && (
      <Button
        onClick={() => {
          reset(getDefaultValues(participant))
          if (participant) {
            setOpen(false)
          } else {
            // destroy the card altogether
            handlePostAction?.()
          }
        }}
        variant='tertiary-deemphasized'
        disabled={mutation.isPending}
      >
        Cancel
      </Button>
    )

    const submitButton = (
      <Button
        type='submit'
        variant='secondary'
        disabled={
          (participant && !isDirty) ||
          mutation.isPending ||
          shouldDisableSubmitButton()
        }
        onClick={() => {
          const eventName = participant ? '[EE] Add dependent clicked' : '[EE] Edit dependent clicked'
          window.segment?.track(eventName, {
            dependent_relationship: participant?.relationship,
            dependent_has_disability: participant?.has_disability,
            participant_id: participant?.id,
          })
        }}
      >
        {participant ? 'Edit dependent' : 'Add dependent'}
      </Button>
    )

    if (breakpointIsAtLeast('xs')) {
      return (
        <ButtonGroup size='large'>
          {removeButton}
          {cancelButton}
          {submitButton}
        </ButtonGroup>
      )
    }

    return (
      <ButtonGroup size='large' layout='vertical'>
        {submitButton}
        {cancelButton}
        {removeButton}
      </ButtonGroup>
    )
  }

  // Green checkmark logic
  const shouldShowCheckmark = participant && allInfoValid && !isDirty
  useEffect(() => {
    if (shouldShowCheckmark) {
      onInfoSaved?.(true)
    } else {
      onInfoSaved?.(false) // Pass up to parent to disable the Next button if any info isn't saved
    }
  }, [shouldShowCheckmark])

  // CoveredParticipantCard
  return (
    // If the form is dirty (i.e. user touched something), then get rid of the checkmark and disable the edit button
    <CollapsibleContainer
      open={open}
      onOpenChange={(isOpen) => setOpen(isOpen)}
      heading={
        participant
          ? `${participant.given_name} (${participant.relationship})`
          : 'New dependent'
      }
      headingIconContent={
      shouldShowCheckmark && (
        <div className='text-green-400'>
          <SolidCircleCheckIcon aria-label='(complete)' size={16} />
        </div>
      )
      }
    >
      <form
        data-testid={!participant ? 'add-dependent-card' : 'edit-dependent-card'}
        onSubmit={handleSubmit(maybeSubmitForm)}
        onBlur={(e) => { // Save dependent changes onBlur
          if (!isDirty) return // Ignore resubmitting when nothing changed
          if (e.currentTarget.contains(e.relatedTarget as Node)) return // Ignore blurs between inputs
          handleSubmit(maybeSubmitForm)()
        }}
      >
        <div className='p-4 space-y-4'>
          <div className='flex flex-col xs:flex-row gap-4'>
            {/* First name */}
            <FormTextFieldDS<ParticipantFormProps>
              error={errors.given_name}
              fieldId='given_name'
              fieldLabel='First name'
              register={register}
              placeholder='E.g. Jane'
              disabled={mutation.isPending}
              validationRule={{ required: 'First name is required.' }}
            />
            {/* Last name */}
            <FormTextFieldDS<ParticipantFormProps>
              error={errors.family_name}
              fieldId='family_name'
              fieldLabel='Last name'
              register={register}
              placeholder='E.g. Doe'
              disabled={mutation.isPending}
              validationRule={{ required: 'Last name is required.' }}
            />
            {/* Date of birth */}
            <FormDateFieldDS<ParticipantFormProps>
              error={errors.dob}
              fieldId='dob'
              fieldLabel='Date of birth'
              register={register}
              placeholder='YYYY-MM-DD'
              disabled={mutation.isPending}
              withPortal={false}
              validationRule={{
                required: 'Date of birth is required.',
                validate: {
                  validYear: (value: string) => {
                    const age = calculateAgeAtDate(value, new Date())
                    if (age > 120) {
                      return 'Date of birth cannot be more than 120 years ago'
                    } else if (age < 0) {
                      return 'Date of birth cannot be in the future'
                    }
                    return true
                  },
                },
              }}
            />
          </div>
          {/* Spouse/Child radios -- only show tooltip for Spouse radio when disabled */}
          <div>
            <Tooltip
              content="You've already added a spouse"
              enabled={isSpouseOptionDisabled}
              side='top'
              align='start'
              arrowAlign='start'
            >
              <div>
                <FormRadioGroupDS
                  control={control}
                  name='relationship'
                  label='Relationship'
                  options={[
                    {
                      label: 'Spouse',
                      value: 'spouse',
                      disabled: isSpouseOptionDisabled,
                    },
                    { label: 'Child', value: 'child' },
                  ]}
                  disabled={mutation.isPending}
                />
              </div>
            </Tooltip>
          </div>
          {/* Disability checkbox */}
          {showDisabilityCheckbox && soonestCoverageStartDate && (
            <Banner
              title={`${
                givenName || 'Your child'
              } will be over 25 by ${getFormattedDate(
                soonestCoverageStartDate,
                { month: 'long', timeZone: 'UTC' }
              )}`}
              type='warning'
            >
              <p>
                Due to their age, {givenName || 'your child'} can only be added
                as a dependent on your plan if they have a disability.
              </p>
              <FormCheckboxFieldDS
                fieldId='has_disability'
                fieldLabel='Exempt from age limit due to disability'
                register={register}
                disabled={mutation.isPending}
                error={errors.has_disability}
              />
            </Banner>
          )}
        </div>
        {/* Error on submit */}
        {submitError && (
          <div className='mb-10'>
            <Banner
              title='A problem has occurred while processing your request'
              type='critical'
            >
              {submitError}
            </Banner>
          </div>
        )}
        <div className='p-4 border-t border-gray-300'>{renderButtons()}</div>
      </form>
    </CollapsibleContainer>
  )
}
